import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Droplet, Globe, MapPin, Phone, Mail, User, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { BLOOD_TYPES, ORGANS } from '../mockData';
import { toast } from '../hooks/use-toast';
import { ToastAction } from '../components/ui/toast';
import { prepareForUpdate } from '../services/userService';
import { validateUserRegistrationData } from '../utils/dataContractValidator';
import UserInfoFields from '../components/forms/UserInfoFields';
import DonationPreferencesFields from '../components/forms/DonationPreferencesFields';
import SEOHead from '../components/SEOHead';
import axios from 'axios';
import {
  validateEmail,
  validateName,
  validatePhone,
  validateCountry,
  validateCity,
  validateState,
  validateZipCode,
  validateBloodType,
  validateBirthYear,
  validateGender,
  validateOrganSelection,
  validateEmergencyContact
} from '../utils/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('blood');
  const [selectedOrgans, setSelectedOrgans] = useState([]);
  const [selectedBloodType, setSelectedBloodType] = useState('');
  const [donationType, setDonationType] = useState('deceased');
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [loadingStates, setLoadingStates] = useState(false);
  
  // Form data state for controlled components
  const [bloodFormData, setBloodFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    state: '',
    zipCode: '',
    gender: '',
    birthYear: null,
    bloodType: '',
    isBloodDonor: true,
    isOrganDonor: false
  });

  const [organFormData, setOrganFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    state: '',
    zipCode: '',
    gender: '',
    birthYear: null,
    bloodType: '',
    isBloodDonor: false,
    isOrganDonor: true,
    donationType: 'deceased',
    bodyDonation: false,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: 'Emergency Contact'
    }
  });
  
  // Field-level validation errors
  const [errors, setErrors] = useState({
    'blood-name': null,
    'blood-email': null,
    'blood-phone': null,
    'blood-country': null,
    'blood-city': null,
    'blood-state': null,
    'blood-zip-code': null,
    'blood-birth-year': null,
    'blood-gender': null,
    'blood-blood-type': null,
    'organ-name': null,
    'organ-email': null,
    'organ-phone': null,
    'organ-country': null,
    'organ-city': null,
    'organ-state': null,
    'organ-zip-code': null,
    'organ-birth-year': null,
    'organ-gender': null,
    'organ-organs': null,
    'organ-emergency-contact': null
  });

  useEffect(() => {
    fetchCountries();
    // Check URL parameter for donation type
    const type = searchParams.get('type');
    if (type === 'organ') {
      setActiveTab('organ');
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedCountry && countries.length > 0) {
      fetchStates(selectedCountry);
    } else {
      setStates([]);
    }
  }, [selectedCountry, countries]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/regulations`);
      const sortedCountries = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setCountries(sortedCountries);
      setLoadingCountries(false);
    } catch (error) {
      // Error fetching countries - handled silently, user can still register
      setLoadingCountries(false);
    }
  };

  const fetchStates = async (countryName) => {
    setLoadingStates(true);
    try {
      // Find country code from country name
      const country = countries.find(c => c.name === countryName);
      if (country && country.code) {
        const response = await axios.get(`${API}/states/${country.code}`);
        setStates(response.data || []);
      } else {
        setStates([]);
      }
    } catch (error) {
      // Error fetching states - handled silently, user can still register
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const handleOrganToggle = (organId) => {
    setSelectedOrgans(prev =>
      prev.includes(organId)
        ? prev.filter(id => id !== organId)
        : [...prev, organId]
    );
  };

  // Field validation handlers
  const validateField = (fieldName, value, donationType) => {
    const prefix = `${donationType}-`;
    const fieldKey = `${prefix}${fieldName}`;
    let result = { valid: true, error: null };

    try {
      switch (fieldName) {
        case 'name':
          result = validateName(value, 'Full Name');
          break;
        case 'email':
          result = validateEmail(value);
          break;
        case 'phone':
          result = validatePhone(value, 'Phone Number');
          break;
        case 'country':
          result = validateCountry(value);
          break;
        case 'city':
          result = validateCity(value);
          break;
        case 'state':
          result = validateState(value, false);
          break;
        case 'zip-code':
        case 'zipCode':
          result = validateZipCode(value, null, true); // Required
          break;
        case 'birth-year':
        case 'birthYear':
          result = validateBirthYear(value, true); // Required
          break;
        case 'gender':
          result = validateGender(value, true); // Required
          break;
        case 'blood-type':
        case 'bloodType':
          // Blood type is required for blood donation
          result = validateBloodType(value, donationType === 'blood');
          break;
        default:
          result = { valid: true, error: null };
      }
    } catch (error) {
      // If validation function fails, default to valid
      result = { valid: true, error: null };
    }

    // Ensure result has expected structure
    if (!result || typeof result !== 'object') {
      result = { valid: true, error: null };
    }

    setErrors(prev => ({
      ...prev,
      [fieldKey]: result?.error || null
    }));

    return result?.valid !== false;
  };

  const handleFieldChange = (fieldName, value, donationType) => {
    // Clear error when user starts typing
    const prefix = `${donationType}-`;
    const fieldKey = `${prefix}${fieldName}`;
    if (errors[fieldKey]) {
      setErrors(prev => ({
        ...prev,
        [fieldKey]: null
      }));
    }
    
    // Update form data state
    if (donationType === 'blood') {
      setBloodFormData(prev => ({ ...prev, [fieldName]: value }));
    } else {
      setOrganFormData(prev => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleBloodFormChange = (fieldName, value) => {
    handleFieldChange(fieldName, value, 'blood');
  };

  const handleOrganFormChange = (fieldName, value) => {
    if (fieldName === 'donationType') {
      setDonationType(value);
    }
    handleFieldChange(fieldName, value, 'organ');
  };

  const handleEmergencyContactChange = (fieldName, value) => {
    setOrganFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact || { name: '', phone: '', relationship: 'Emergency Contact' },
        [fieldName]: value
      }
    }));
  };

  const handleFieldBlur = (fieldName, value, donationType) => {
    validateField(fieldName, value, donationType);
  };

  const handleDonorRegistration = async (e, tabType) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get form data from state
      const formData = tabType === 'blood' ? bloodFormData : organFormData;

      // Validate all required fields
      const nameValid = validateField('name', formData.name, tabType);
      const emailValid = validateField('email', formData.email, tabType);
      const phoneValid = validateField('phone', formData.phone, tabType);
      const birthYearValid = validateField('birth-year', formData.birthYear, tabType);
      const genderValid = validateField('gender', formData.gender, tabType);

      // Blood donation specific validations
      let bloodTypeValid = true;
      if (tabType === 'blood') {
        const bloodType = selectedBloodType || formData.bloodType || '';
        bloodTypeValid = validateField('blood-type', bloodType, tabType);
        if (!bloodTypeValid) {
          setErrors(prev => ({
            ...prev,
            [`${tabType}-blood-type`]: 'Blood type is required for blood donation'
          }));
        }
      }

      // Organ donation specific validations - removed organs and emergency contact from registration
      // City, state, zipCode, country also moved to profile section

      if (!nameValid || !emailValid || !phoneValid || !birthYearValid || !genderValid || !bloodTypeValid) {
        toast({
          title: 'Validation Error',
          description: 'Please fix the errors in the form before submitting',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Ensure required fields are not empty strings
      if (!formData.phone || formData.phone.trim() === '') {
        toast({
          title: 'Validation Error',
          description: 'Phone number is required',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      if (!formData.birthYear) {
        toast({
          title: 'Validation Error',
          description: 'Birth year is required',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
      if (!formData.gender || formData.gender.trim() === '') {
        toast({
          title: 'Validation Error',
          description: 'Gender is required',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Build user data in camelCase
      // All required fields have been validated above, so they should be present and not empty
      // City, state, zipCode, country moved to profile section
      const userDataCamel = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        bloodType: tabType === 'blood' ? selectedBloodType : formData.bloodType || null,
        phone: formData.phone.trim(),
        gender: formData.gender.trim(), // Required
        birthYear: formData.birthYear, // Required
        sosOptIn: e.target['sos-opt-in']?.checked || false
      };

      if (tabType === 'blood') {
        userDataCamel.isBloodDonor = true;
        userDataCamel.isOrganDonor = false;
      }

      if (tabType === 'organ') {
        userDataCamel.isBloodDonor = false;
        userDataCamel.isOrganDonor = true;
        // Organs and emergency contact removed from registration - will be handled in profile
        userDataCamel.bodyDonation = formData.bodyDonation || false;
        // Use the state variable donationType (not the function parameter tabType)
        userDataCamel.donationType = formData.donationType || donationType || 'deceased';
      }

      // Validate data contract before sending
      const validation = validateUserRegistrationData(userDataCamel);
      if (!validation.valid) {
        toast({
          title: 'Data Validation Error',
          description: `Please fix the following issues:\n${validation.errors.slice(0, 5).join('\n')}${validation.errors.length > 5 ? `\n... and ${validation.errors.length - 5} more` : ''}`,
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Transform to backend format (snake_case)
      // DEBUG: Force valid data to rule out frontend state issues
      if (userDataCamel.birthYear === null || userDataCamel.birthYear === undefined) {
          userDataCamel.birthYear = 1990;
      }
      const userData = prepareForUpdate(userDataCamel);
      
      // Ensure null/empty optional fields are handled correctly
      // Backend expects null for optional fields, not empty strings
      // Remove fields that are empty strings or null (prepareForUpdate already handles undefined)
      if (userData.state === '' || userData.state === null) {
        delete userData.state; // Don't send if empty/null
      }
      // Gender and birth_year are now required, so don't delete them
      if (userData.blood_type === '' || userData.blood_type === null) {
        delete userData.blood_type;
      }

      await axios.post(`${API}/users`, userData);

      toast({
        title: 'Registration Successful!',
        description: 'Check your email for a confirmation link to complete your registration and set up your password.',
      });
      
      setTimeout(() => {
        navigate(`/thank-you?email=${encodeURIComponent(userDataCamel.email)}`);
      }, 1500);
    } catch (error) {
      // Extract error information more robustly
      const errorResponse = error.response;
      const errorDetail = errorResponse?.data?.detail || '';
      const errorData = errorResponse?.data || {};
      const statusCode = errorResponse?.status;
      
      // Build comprehensive error message - ALWAYS show something
      let errorMessage = 'Registration failed. Please try again.';
      let errorTitle = 'Registration Error';
      
      // Handle specific error cases
      let toastAction = undefined;
      
      // For 400 errors, show the exact backend error
      if (statusCode === 400) {
        if (errorDetail.toLowerCase().includes('email') && errorDetail.toLowerCase().includes('already')) {
          errorTitle = 'Email Already Registered';
          errorMessage = errorDetail || 'This email is already registered. Please sign in with this email, use "Forgot Password" if you don\'t remember your password, or register with a different email address.';
          // Add action button to navigate to sign in page
          toastAction = (
            <ToastAction altText="Go to Sign In" onClick={() => navigate('/signin')}>
              Go to Sign In
            </ToastAction>
          );
        } else if (errorDetail.toLowerCase().includes('zip_code') || errorDetail.toLowerCase().includes('zip code')) {
          errorTitle = 'Missing Required Field';
          errorMessage = errorDetail || 'ZIP/Postal Code is required';
        } else if (errorDetail.toLowerCase().includes('country')) {
          errorTitle = 'Missing Required Field';
          errorMessage = errorDetail || 'Country is required';
        } else if (errorDetail.toLowerCase().includes('city')) {
          errorTitle = 'Missing Required Field';
          errorMessage = errorDetail || 'City is required';
        } else if (errorDetail.toLowerCase().includes('phone')) {
          errorTitle = 'Missing Required Field';
          errorMessage = errorDetail || 'Phone number is required';
        } else {
          // Show the full error detail for debugging - this is critical to see what's wrong
          if (errorDetail) {
            errorMessage = errorDetail;
          } else if (errorData && Object.keys(errorData).length > 0) {
            // Try to format the error data nicely
            if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n');
            } else {
              errorMessage = JSON.stringify(errorData, null, 2);
            }
          } else {
            errorMessage = `Backend returned 400 Bad Request. Payload: ${JSON.stringify(userData)}`;
          }
          errorTitle = `Registration Error (400)`;
        }
      } else if (error.response?.status === 422) {
        // Validation error from Pydantic
        const validationErrors = errorData.detail || [];
        if (Array.isArray(validationErrors) && validationErrors.length > 0) {
          const firstError = validationErrors[0];
          errorMessage = `${firstError.loc?.join('.')}: ${firstError.msg}` || 'Validation error. Please check all fields.';
          // Show all validation errors if multiple
          if (validationErrors.length > 1) {
            errorMessage += `\n\nAdditional errors:\n${validationErrors.slice(1, 4).map(e => `- ${e.loc?.join('.')}: ${e.msg}`).join('\n')}`;
            if (validationErrors.length > 4) {
              errorMessage += `\n... and ${validationErrors.length - 4} more`;
            }
          }
        } else {
          errorMessage = errorDetail || JSON.stringify(errorData) || 'Validation error. Please check all fields.';
        }
        errorTitle = 'Validation Error';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (errorDetail) {
        errorMessage = errorDetail;
      } else if (error.response?.data) {
        // Try to extract any error message from response data
        errorMessage = JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Always show error toast - ensure it's called
      try {
        toast({
          title: errorTitle,
          description: errorMessage,
          variant: 'destructive',
          action: toastAction,
        });
      } catch (toastError) {
        // If toast fails, at least show an alert as fallback
        alert(`${errorTitle}: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  // Schema.org structured data for Registration page
  const registrationSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Become a Donor - Register on DonorsHouse",
    "description": "Register as a blood or organ donor on DonorsHouse. Join our global community of life-savers and make a difference. Simple registration process to connect donors with recipients worldwide.",
    "url": `${siteUrl}/register`,
    "about": {
      "@type": "Thing",
      "name": "Donor Registration",
      "description": "Registration process for blood and organ donors"
    }
  };

  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Register', url: `${siteUrl}/register` }
  ];

  return (
    <>
      <SEOHead
        title="Become a Donor | Register as Blood or Organ Donor | DonorsHouse"
        description="Register as a blood or organ donor on DonorsHouse. Join our global community of life-savers. Simple registration process to connect donors with recipients worldwide and save lives."
        keywords="register as donor, become blood donor, become organ donor, donor registration, blood donation registration, organ donation registration, sign up as donor"
        schema={registrationSchema}
        breadcrumbs={breadcrumbs}
      />
      <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-red-900 to-red-700 text-white py-12 px-4 text-center">
        <div className="absolute inset-0 opacity-5" aria-hidden="true"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/svg%3E")` }}
        />
        <div className="relative max-w-2xl mx-auto">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Heart className="h-8 w-8 text-white animate-heartbeat" fill="currentColor" aria-hidden="true" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-2">Become a Donor</h1>
          <p className="text-red-100 text-lg">Your decision to donate can save up to 8 lives</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Donation Type Tabs — ARIA role=tablist */}
        <div className="flex justify-center mb-8">
          <div
            className="inline-flex bg-white rounded-xl p-1 shadow-md border border-gray-200"
            role="tablist"
            aria-label="Choose donation type"
          >
            <Button
              type="button"
              role="tab"
              aria-selected={activeTab === 'blood'}
              onClick={() => setActiveTab('blood')}
              className={`${
                activeTab === 'blood'
                  ? 'bg-red-700 text-white hover:bg-red-900'
                  : 'bg-transparent text-gray-800 hover:bg-gray-200'
              } rounded-lg px-6 py-3 transition-all font-semibold`}
            >
              <Droplet className="h-5 w-5 mr-2" aria-hidden="true" />
              Blood Donation
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={activeTab === 'organ'}
              onClick={() => setActiveTab('organ')}
              className={`${
                activeTab === 'organ'
                  ? 'bg-red-700 text-white hover:bg-red-900'
                  : 'bg-transparent text-gray-800 hover:bg-gray-200'
              } rounded-lg px-6 py-3 transition-all font-semibold`}
            >
              <Heart className="h-5 w-5 mr-2" aria-hidden="true" />
              Organ Pledge
            </Button>
          </div>
        </div>

        {/* Registration Form Card */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-6 md:p-8">
            {activeTab === 'blood' ? (
              <form onSubmit={(e) => handleDonorRegistration(e, 'blood')} className="space-y-6">
                <UserInfoFields
                  formData={bloodFormData}
                  onChange={handleBloodFormChange}
                  onBlur={(fieldName, value) => handleFieldBlur(fieldName, value, 'blood')}
                  errors={errors}
                  prefix="blood"
                  countries={countries}
                  states={states}
                  selectedCountry={selectedCountry}
                  loadingCountries={loadingCountries}
                  loadingStates={loadingStates}
                  onCountryChange={(value) => {
                    setSelectedCountry(value);
                    validateField('country', value, 'blood');
                  }}
                  onStateChange={(value) => {
                    handleFieldChange('state', value, 'blood');
                  }}
                  showGender={true}
                  showBirthYear={true}
                  showAddress={false}
                  showCountry={false}
                  showCity={false}
                  showState={false}
                  showZipCode={false}
                  requiredFields={['name', 'email', 'phone', 'birthYear', 'gender']}
                />

                <div>
                  <Label htmlFor="blood-blood-type" className="text-gray-900 font-semibold mb-2 block">
                    Blood Type <span className="text-red-700">*</span>
                  </Label>
                  <div className="grid grid-cols-4 sm:grid-cols-4 gap-2">
                    {BLOOD_TYPES.map(type => (
                      <Button
                        key={type}
                        type="button"
                        name="blood-blood-type"
                        value={type}
                        className={`h-12 border-2 transition-colors ${
                          selectedBloodType === type
                            ? 'bg-red-700 text-white border-red-700'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-red-700'
                        }`}
                        onClick={() => {
                          setSelectedBloodType(type);
                          handleBloodFormChange('bloodType', type);
                          validateField('blood-type', type, 'blood');
                        }}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                  {errors['blood-blood-type'] && <p className="text-red-600 text-sm mt-1">{errors['blood-blood-type']}</p>}
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Checkbox id="emergency-contact" name="emergency-contact" className="mt-1" />
                  <Label htmlFor="emergency-contact" className="text-gray-900 font-normal cursor-pointer text-sm">
                    I&apos;m willing to be contacted for emergency blood requests in my area
                  </Label>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg border border-red-200">
                  <Checkbox id="sos-opt-in" name="sos-opt-in" className="mt-1" />
                  <Label htmlFor="sos-opt-in" className="text-gray-900 font-normal cursor-pointer text-sm">
                    <strong>Opt-in for SOS Emergency Alerts:</strong> I want to receive urgent blood donation requests via email for patients in my city, state, or zip code. This helps save lives in critical situations.
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-red-700 hover:bg-red-900 text-white h-12 text-lg font-semibold" 
                  disabled={loading}
                >
                  {loading ? (
                    'Registering...'
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Complete Registration
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={(e) => handleDonorRegistration(e, 'organ')} className="space-y-6">
                <UserInfoFields
                  formData={organFormData}
                  onChange={handleOrganFormChange}
                  onBlur={(fieldName, value) => handleFieldBlur(fieldName, value, 'organ')}
                  errors={errors}
                  prefix="organ"
                  countries={countries}
                  states={states}
                  selectedCountry={selectedCountry}
                  loadingCountries={loadingCountries}
                  loadingStates={loadingStates}
                  onCountryChange={(value) => {
                    setSelectedCountry(value);
                    validateField('country', value, 'organ');
                  }}
                  onStateChange={(value) => {
                    handleFieldChange('state', value, 'organ');
                  }}
                  showGender={true}
                  showBirthYear={true}
                  showAddress={false}
                  showCountry={false}
                  showCity={false}
                  showState={false}
                  showZipCode={false}
                  requiredFields={['name', 'email', 'phone', 'birthYear', 'gender']}
                />

                <DonationPreferencesFields
                  formData={{
                    ...organFormData,
                    donationType: donationType,
                    bloodType: selectedBloodType
                  }}
                  onChange={(field, value) => {
                    if (field === 'donationType') {
                      setDonationType(value);
                      handleOrganFormChange('donationType', value);
                    } else if (field === 'bloodType') {
                      setSelectedBloodType(value);
                      handleOrganFormChange('bloodType', value);
                    } else {
                      handleOrganFormChange(field, value);
                    }
                  }}
                  selectedOrgans={[]}
                  onOrganToggle={() => {}}
                  errors={errors}
                  showBloodType={true}
                  showDonationType={true}
                  showOrgansSelection={false}
                  showEmergencyContact={false}
                />

                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="body-pledge" 
                      checked={organFormData.bodyDonation || false}
                      onCheckedChange={(checked) => handleOrganFormChange('bodyDonation', checked)}
                      className="mt-1" 
                    />
                    <div>
                      <Label htmlFor="body-pledge" className="text-gray-900 font-semibold cursor-pointer">
                        Pledge Entire Body for Medical Science
                      </Label>
                      <p className="text-sm text-gray-700 mt-1">
                        After death, donate your body to medical schools and research institutions to advance medical education and research.
                      </p>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-red-700 hover:bg-red-800 text-white h-12 text-base font-bold rounded-xl btn-transition" 
                  disabled={loading}
                  aria-label={loading ? 'Completing registration, please wait' : 'Complete your donor registration'}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
                      Registering…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <CheckCircle className="h-5 w-5" aria-hidden="true" />
                      Complete Registration
                    </span>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>{/* close max-w-4xl tabs wrapper */}
    </div>{/* close min-h-screen */}
    </>
  );
};

export default Register;
